import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logout, Photo, Shirt } from 'tabler-icons-react';
import { Image } from '@mantine/core';
import roundHalfEven from 'round-half-even';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import BulkEditView from '../bulkEdit/BulkEditView';
import {
  BULK_EDIT_FIELD_TYPE_ENUM,
  WEIGHT_UNIT_ENUM,
  WEIGHT_UNIT_LIST
} from '../../../config/constants';
import AppFlexbox from '../../common/AppFlexbox';
import { sortVariantsByOptions } from '../../../helpers/webstoreHelper';
import { useAdminBackPath } from '../../../helpers/adminHelper';
import { PRODUCT_CATEGORY_LIST } from '../../../config/productCategories';

const PRODUCT_DATA_FIELDS = {
  title: 'title',
  description: 'description',
  fkEcomCategory: 'fkEcomCategory',
  productType: 'productType'
};

const VARIANT__DATA_FIELDS = {
  price: 'price',
  sku: 'sku',
  weight: 'weight',
  weightUnit: 'weightUnit'
};

const PRODUCT_FIELDS = {
  title: 0,
  description: 1,
  fkEcomCategory: 2,
  productType: 3,
  usesGarmentCanvas: 4,
  markupPercentage: 5
};

const VARIANT_FIELDS = {
  price: 6,
  markupDollar: 7,
  markupPrice: 8,
  sku: 9,
  weight: 10,
  weightUnit: 11
};

const EDIT_VARIANT_FIELDS = [
  {
    value: 'price',
    label: 'Price',
    menuLabel: 'Base Price',
    dataField: VARIANT__DATA_FIELDS.price,
    type: BULK_EDIT_FIELD_TYPE_ENUM.PRICE,
    displayed: true,
    required: true,
    category: 'Variant'
  },
  {
    value: 'markupDollar',
    label: 'Markup adjustment $',
    dataField: PRODUCT_DATA_FIELDS.markupDollar,
    type: BULK_EDIT_FIELD_TYPE_ENUM.TEXT_PRICE,
    displayed: true,
    category: 'variant'
  },
  {
    value: 'markupPrice',
    label: 'Markup price',
    dataField: PRODUCT_DATA_FIELDS.markupPrice,
    type: BULK_EDIT_FIELD_TYPE_ENUM.TEXT_PRICE,
    displayed: true,
    category: 'variant'
  },
  {
    value: 'sku',
    label: 'SKU',
    displayed: true,
    dataField: VARIANT__DATA_FIELDS.sku,
    type: BULK_EDIT_FIELD_TYPE_ENUM.TEXT_INPUT,
    category: 'Variant'
  },
  {
    value: 'weight',
    label: 'Weight',
    displayed: true,
    menuLabel: 'Weight and shipping',
    dataField: VARIANT__DATA_FIELDS.weight,
    type: BULK_EDIT_FIELD_TYPE_ENUM.NUMBER,
    category: 'Variant'
  },
  {
    value: 'weightUnit',
    label: 'Weight unit',
    displayed: true,
    dataField: VARIANT__DATA_FIELDS.weightUnit,
    type: BULK_EDIT_FIELD_TYPE_ENUM.SELECT,
    options: WEIGHT_UNIT_LIST,
    groupedField: 'weight',
    category: 'Variant'
  }
];

const EDIT_PRODUCT_FIELDS = [
  {
    value: 'title',
    label: 'Title',
    dataField: PRODUCT_DATA_FIELDS.title,
    type: BULK_EDIT_FIELD_TYPE_ENUM.TEXT_INPUT,
    displayed: true,
    staticDisplay: true,
    category: 'Product',
    firstPosition: true,
    leftSectionWidth: 70,
    noBulkEdit: true,
    getLeftSection: (altValue) => (
      <AppFlexbox
        style={{
          width: 25,
          height: 25,
          borderRadius: 5,
          boxSizing: 'border-box',
          border: 'solid 1px #dee2e6 ',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {altValue ? (
          <Image src={altValue} style={{ width: '100%', height: '100%' }} />
        ) : (
          <Photo color="#dee2e6" size={14} />
        )}
      </AppFlexbox>
    )
  },
  {
    value: 'description',
    label: 'Description',
    dataField: PRODUCT_DATA_FIELDS.description,
    type: BULK_EDIT_FIELD_TYPE_ENUM.TEXT_INPUT,
    productOnly: true,
    category: 'Product'
  },
  {
    value: 'category',
    label: 'Category',
    dataField: PRODUCT_DATA_FIELDS.fkEcomCategory,
    type: BULK_EDIT_FIELD_TYPE_ENUM.SELECT,
    getOptions: (optionsState) => optionsState.ecomCategories,
    productOnly: true,
    displayed: true,
    clearable: true,
    category: 'Product',
    defaultValue: null
  },
  {
    value: 'productType',
    label: 'Sub category',
    dataField: PRODUCT_DATA_FIELDS.productType,
    type: BULK_EDIT_FIELD_TYPE_ENUM.TEXT_INPUT,
    productOnly: true,
    displayed: true,
    category: 'Product'
  },
  {
    value: 'usesGarmentCanvas',
    label: 'Uses Garment Canvas',
    dataField: PRODUCT_DATA_FIELDS.usesGarmentCanvas,
    type: BULK_EDIT_FIELD_TYPE_ENUM.CHECKBOX,
    productOnly: true,
    displayed: false,
    category: 'Product'
  },
  {
    value: 'markupPercentage',
    label: 'Markup %',
    dataField: PRODUCT_DATA_FIELDS.markupPercentage,
    type: BULK_EDIT_FIELD_TYPE_ENUM.PRICE,
    productOnly: true,
    displayed: true,
    category: 'Product',
    prefix: '',
    suffix: '%'
  },
  ...EDIT_VARIANT_FIELDS
];

const mapVariantToForm = (variant, productTitle = null) => ({
  key: variant.uuid.toString(),
  pkEcomExternalProductVariant: variant.pkEcomExternalProductVariant.toString(),
  uuid: variant.uuid,
  externalId: variant.externalId,
  columns: [
    {
      key: PRODUCT_FIELDS.title,
      value: `${
        productTitle ? `${productTitle} - ` : ''
      }${variant.ecomVendorProductVariantOptions
        .sort((a, b) => a.nameSort - b.nameSort)
        .map((s) => s.value)
        .join(' / ')}`,
      altValue: variant.previewImage,
      disabled: true
    },
    {
      key: PRODUCT_FIELDS.description,
      value: '',
      disabled: true
    },
    {
      key: PRODUCT_FIELDS.fkEcomCategory,
      value: '',
      disabled: true
    },
    {
      key: PRODUCT_FIELDS.productType,
      value: '',
      disabled: true
    },
    {
      key: PRODUCT_FIELDS.usesGarmentCanvas,
      value: '',
      disabled: true
    },
    {
      key: PRODUCT_FIELDS.markupPercentage,
      value: '',
      disabled: true
    },
    {
      key: VARIANT_FIELDS.price,
      value: variant.price / 100
    },
    {
      key: VARIANT_FIELDS.markupDollar,
      getValue: (variantColumns, productColumns) => {
        const markupPercentage =
          productColumns[PRODUCT_FIELDS.markupPercentage]?.value;
        const price = variantColumns[VARIANT_FIELDS.price]?.value;

        return markupPercentage && price
          ? price + roundHalfEven(price * (markupPercentage / 100), 0) - price
          : '';
      },
      prefix: '',
      suffix: '%',
      disabled: true
    },
    {
      key: VARIANT_FIELDS.markupPrice,
      getValue: (variantColumns, productColumns) => {
        const markupPercentage =
          productColumns[PRODUCT_FIELDS.markupPercentage]?.value;
        const price = variantColumns[VARIANT_FIELDS.price]?.value;

        return markupPercentage && price
          ? price + roundHalfEven(price * (markupPercentage / 100), 0)
          : '';
      },
      prefix: '',
      suffix: '%',
      disabled: true
    },
    {
      key: VARIANT_FIELDS.sku,
      value: variant.sku
    },
    {
      key: VARIANT_FIELDS.weight,
      value: variant.weight
    },
    {
      key: VARIANT_FIELDS.weightUnit,
      value: WEIGHT_UNIT_ENUM.G,
      disabled: true
    }
  ]
});

const mapProductToForm = (ecomVendorProduct, variantUuids = []) => ({
  key: ecomVendorProduct.pkEcomExternalProduct.toString(),
  pkEcomExternalProduct: ecomVendorProduct.pkEcomExternalProduct.toString(),
  uuid: ecomVendorProduct.uuid,
  columns: [
    {
      key: PRODUCT_FIELDS.title,
      value: ecomVendorProduct.name,
      altValue: ecomVendorProduct.previewImage
    },
    {
      key: PRODUCT_FIELDS.description,
      value: ecomVendorProduct.description
    },
    {
      key: PRODUCT_FIELDS.fkEcomCategory,
      value: ecomVendorProduct.fkEcomCategory?.toString() ?? ''
    },
    {
      key: PRODUCT_FIELDS.productType,
      value: ecomVendorProduct.productType
    },
    {
      key: PRODUCT_FIELDS.usesGarmentCanvas,
      value: ecomVendorProduct.usesGarmentCanvas
    },
    {
      key: PRODUCT_FIELDS.markupPercentage,
      value: ecomVendorProduct.markupPercentage,
      prefix: '',
      suffix: '%'
    },
    ...EDIT_VARIANT_FIELDS.map((f) => ({
      key: f.value,
      value: '',
      disabled: true
    }))
  ],
  subrows: [
    ...sortVariantsByOptions(ecomVendorProduct.ecomVendorProductVariants)
      .filter((v) => !variantUuids.length || variantUuids.includes(v.uuid))
      .map((v) =>
        mapVariantToForm(v, variantUuids.length ? ecomVendorProduct.name : null)
      )
  ]
});

const AdminBulkEditProductsView = () => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    state,
    fetchEcomExternalProductExportData,
    updateEcomExternalProducts
  } = useContext(AdminContext);
  const [formState, setFormState] = useState({ loading: false });
  const searchParams = new URLSearchParams(search);
  const productIds = searchParams.get('ids')?.split(',') ?? [];
  const searchParam = searchParams.get('search') ?? '';
  const statusParam = searchParams.get('status') ?? '';
  const categoryParam = searchParams.get('category') ?? '';
  const variantUuids = searchParams.get('variants')?.split(',') ?? [];
  const { backPath } = useAdminBackPath(
    variantUuids.length > 0
      ? `/admin/products/${productIds[0]}`
      : '/admin/products'
  );

  const selectedProducts = state.ecomExternalProductsExport.value.filter(
    (p) => productIds.length === 0 || productIds.includes(p.uuid)
  );

  const selectedVariants =
    variantUuids && variantUuids.length > 0
      ? selectedProducts
          .map((s) => s.ecomVendorProductVariants)
          .flat()
          .filter((v) => variantUuids.includes(v.uuid))
      : [];

  useEffect(() => {
    fetchEcomExternalProductExportData(
      {
        pkEcomExternalProducts: productIds,
        search: searchParam,
        status: statusParam,
        fkCategory: categoryParam
      },
      null,
      (error) => {
        triggerNotification(error);
      }
    );
    hasFetched.current = true;
  }, []);

  const onSaveChanges = (data, onSuccess, onErrorCallback) => {
    setFormState({
      ...formState,
      loading: true
    });
    updateEcomExternalProducts(
      {
        ecomExternalProducts: data
          .filter(
            (d) =>
              d.hasUnsavedChanges || d.subData.some((s) => s.hasUnsavedChanges)
          )
          .map((product) => ({
            pkEcomExternalProduct: product.pkEcomExternalProduct,
            ...(product.hasUnsavedChanges
              ? {
                  ecomVendorProduct: {
                    name: product.name,
                    description: product.description,
                    fkEcomCategory: product.fkEcomCategory,
                    productType: product.productType,
                    usesGarmentCanvas: product.usesGarmentCanvas,
                    markupPercentage: !product.markupPercentage
                      ? 0
                      : product.markupPercentage
                  }
                }
              : {}),
            ecomVendorProductVariants: product.subData
              .filter((s) => s.hasUnsavedChanges)
              .map((variant) => ({
                ...variant,
                price: Math.round(variant.price * 100)
              }))
          }))
      },
      () => {
        onSuccess();
        triggerNotification('Changes saved!', 'success');
        setFormState({
          ...formState,
          loading: false
        });
      },
      (error) => {
        onErrorCallback();
        triggerNotification(error);
        setFormState({
          ...formState,
          loading: false
        });
      }
    );
  };

  return (
    <BulkEditView
      data={selectedProducts
        .sort(
          (a, b) => a.name.localeCompare(b.name) || a.uuid.localeCompare(b.uuid)
        )
        .map((p) => mapProductToForm(p, variantUuids))}
      emptyContent={{
        title: 'No products found',
        description:
          "Oops looks like you haven't selected any products to edit. Try selecting products from your list of products and try again.",
        icon: <Shirt color="#000" size={125} />,
        primaryButton: {
          label: 'Back to products',
          icon: <Logout size={18} style={{ transform: 'rotate(180deg)' }} />,
          link: '/admin/products'
        }
      }}
      fields={EDIT_PRODUCT_FIELDS.map((f) => ({
        ...f,
        displayed: f.productOnly && !!variantUuids.length ? false : f.displayed,
        hidden: f.productOnly && variantUuids.length > 0
      }))}
      loading={!hasFetched.current || state.ecomExternalProductsExport.loading}
      onCancel={() => navigate(backPath)}
      onlySubrows={variantUuids.length > 0}
      onSave={onSaveChanges}
      optionsState={{
        ecomCategories: PRODUCT_CATEGORY_LIST.sort((a, b) =>
          a.name.localeCompare(b.name)
        ).map((c) => ({
          value: c.value.toString(),
          label: c.name
        }))
      }}
      saving={formState.loading}
      title={`Editing ${
        selectedVariants.length
          ? selectedVariants.length
          : selectedProducts.length
      } ${selectedVariants.length ? 'variants' : 'products'}`}
    />
  );
};

AdminBulkEditProductsView.propTypes = {};

export default AdminBulkEditProductsView;
